import type { ReactNode} from 'react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import type { ExperimentClient } from '@amplitude/experiment-js-client';
import { Experiment } from '@amplitude/experiment-js-client';

import { useUser } from '@/hooks/v2/use-user';

import type { FeatureFlagContextType } from './feature-flag-context';
import { FeatureFlagContext } from './feature-flag-context';

interface ProviderProps {
  children: ReactNode;
  config: {
    apiKey: string;
    deploymentKey: string;
  };
}

export const FeatureFlagProvider: React.FC<ProviderProps> = ({ children, config }) => {
  const [experiment, setExperiment] = useState<ExperimentClient | null>(null);
  const [started, setStarted] = useState<boolean>(false);
  const { user } = useUser();
  // Optionally expose loading state for consumers
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    amplitude.init(config.apiKey);

    const initializeExperiment = async () => {
      const experiment = Experiment.initialize(config.deploymentKey);

      await experiment.fetch({
        user_id: user?.id,
        user_properties: {
          email: user?.email || '',
          name: `${user?.first_name  } ${  user?.last_name}` || '',
        },
      });
      // (4) Lookup a flag's variant
      const flagKey = 'v2';
      const variant = experiment.variant(flagKey);
      if (variant.value === 'on') {
        // Flag is on
        setExperiment(experiment);
        setStarted(true);
        setLoading(false);
      } else {
        // Flag is off
        setExperiment(experiment);
        setStarted(true);
        setLoading(false);
      }
    };

    initializeExperiment();
  }, []);

  const isFeatureEnabled = useCallback(
    (flag: string): boolean => {
      // Until experiment is ready, you might want to treat flags as not enabled
      if (!experiment || !started) return false;
      const variant = experiment.variant(flag);
      // Ensure that your active variant isn’t accidentally named "off"
      return variant.value === 'on';
    },
    [experiment, started]
  );

  const refreshFlags = useCallback(async (): Promise<void> => {
    if (!experiment) return;
    try {
      await experiment.fetch();
      console.log('Flags refreshed successfully');
      console.log('experiment', experiment);
    } catch (error) {
      console.error('Error refreshing experiment:', error);
    }
  }, [experiment]);

  return (
    <FeatureFlagContext.Provider value={{ isFeatureEnabled, refreshFlags, loading }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flag utilities.
 */
export function useFeatureFlags(): FeatureFlagContextType {
  return useContext(FeatureFlagContext);
}
